import type { Socket } from 'socket.io-client'
import { postCorporateResources } from './api/PostCorporateResources'
import { applyGeneratedImagesToMergedHtml } from './applyGeneratedImagesToMergedHtml'
import {
  GENERATE_CORPORATE_RESOURCES,
  parseCorporateResourcesGeneration
} from './parseCorporateResourcesGeneration'

const LOG_PREFIX = '[awaitAutoFormatCorporateResources]'
/** Full-unit HTML + image generation routinely exceeds 2 minutes. */
const DEFAULT_TIMEOUT_MS = 300_000

export type AwaitAutoFormatCorporateResourcesParams = {
  projectId: number
  referenceUnitId: string
  html: string
  socket: Socket
  timeoutMs?: number
}

type WaitResult = {
  html: string
  generated_images: NonNullable<
    ReturnType<typeof parseCorporateResourcesGeneration>
  >['generated_images']
}

/**
 * Starts corporate resources generation and waits for matching COMPLETED socket
 * event. Returns finally formatted HTML, or null on any failure (caller falls back).
 */
export async function awaitAutoFormatCorporateResources(
  params: AwaitAutoFormatCorporateResourcesParams
): Promise<string | null> {
  const {
    projectId,
    referenceUnitId,
    html,
    socket,
    timeoutMs = DEFAULT_TIMEOUT_MS
  } = params

  if (!html.trim()) {
    console.log(`${LOG_PREFIX}: empty html, skip`)
    return null
  }

  if (
    !Number.isFinite(projectId) ||
    projectId <= 0 ||
    !referenceUnitId.trim()
  ) {
    console.log(`${LOG_PREFIX}: invalid projectId or referenceUnitId`, {
      projectId,
      referenceUnitId
    })
    return null
  }

  try {
    if (!socket.connected) {
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => {
          socket.off('connect', onConnect)
          reject(new Error('Socket connect timeout'))
        }, Math.min(timeoutMs, 15_000))

        const onConnect = () => {
          clearTimeout(timer)
          resolve()
        }

        socket.once('connect', onConnect)
      })
    }

    socket.emit('join_room', 'mavis')

    // Listen before POST so a fast COMPLETED is not missed.
    const waiter = createGenerationWaiter({
      socket,
      projectId,
      referenceUnitId,
      timeoutMs
    })

    const started = await postCorporateResources({
      project_id: projectId,
      html,
      reference_unit_id: referenceUnitId
    })

    if (!started) {
      console.log(`${LOG_PREFIX}: postCorporateResources returned null`)
      waiter.cancel()
      return null
    }

    waiter.setExpectedGenerationId(Number(started.generation_id))

    const mergedHtml = await waiter.promise
    if (!mergedHtml) return null

    return await applyGeneratedImagesToMergedHtml(
      mergedHtml.html,
      mergedHtml.generated_images
    )
  } catch (err) {
    console.error(`${LOG_PREFIX}: failed`, err)
    return null
  }
}

function createGenerationWaiter(opts: {
  socket: Socket
  projectId: number
  referenceUnitId: string
  timeoutMs: number
}): {
  promise: Promise<WaitResult | null>
  setExpectedGenerationId: (generationId: number) => void
  cancel: () => void
} {
  const { socket, projectId, referenceUnitId, timeoutMs } = opts

  let expectedGenerationId: number | null = null
  const earlyCompleted = new Map<number, WaitResult>()
  let settled = false
  let settle: (value: WaitResult | null) => void = () => undefined

  const promise = new Promise<WaitResult | null>((resolve) => {
    settle = (value) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      socket.off('GENERATION', onGeneration)
      resolve(value)
    }

    const timer = setTimeout(() => {
      console.log(`${LOG_PREFIX}: timed out waiting for GENERATION`, {
        generationId: expectedGenerationId,
        projectId
      })
      settle(null)
    }, timeoutMs)

    const onGeneration = (eventData: { data?: unknown } | unknown) => {
      const raw =
        eventData &&
        typeof eventData === 'object' &&
        'data' in (eventData as Record<string, unknown>)
          ? (eventData as { data?: unknown }).data ?? eventData
          : eventData

      const payload = parseCorporateResourcesGeneration(raw)
      if (!payload || payload.type !== GENERATE_CORPORATE_RESOURCES) return

      const genId = payload.generationId ?? payload.generation_id
      const projId = payload.projectId ?? payload.project_id
      if (
        typeof genId !== 'number' ||
        typeof projId !== 'number' ||
        projId !== projectId
      ) {
        return
      }

      if (
        payload.reference_unit_id &&
        payload.reference_unit_id !== referenceUnitId
      ) {
        return
      }

      const status = String(payload.status ?? '')
      if (status === 'PROCESSING') return

      const matchesExpected =
        expectedGenerationId != null && genId === expectedGenerationId

      if (['FAILED', 'ERROR', 'ENDED'].includes(status)) {
        if (matchesExpected) {
          console.log(`${LOG_PREFIX}: generation failed`, { status })
          settle(null)
        }
        return
      }

      if (status === 'COMPLETED' && payload.mergedHtml) {
        const result: WaitResult = {
          html: payload.mergedHtml,
          generated_images: payload.generated_images ?? []
        }

        if (expectedGenerationId == null) {
          earlyCompleted.set(genId, result)
          return
        }

        if (genId === expectedGenerationId) {
          settle(result)
        }
        return
      }

      if (status === 'COMPLETED' && matchesExpected) {
        console.log(`${LOG_PREFIX}: COMPLETED without mergedHtml`)
      }
    }

    socket.on('GENERATION', onGeneration)
  })

  return {
    promise,
    setExpectedGenerationId: (generationId: number) => {
      expectedGenerationId = generationId
      const buffered = earlyCompleted.get(generationId)
      if (buffered) {
        settle(buffered)
      }
    },
    cancel: () => settle(null)
  }
}
